import * as _ from 'lodash'
import { Variable, VNumber, VArray, all, not, or, and, if as _if, VBoolean } from 'alkali'
import { ElementProperties, Button, Div, Span, Label, H2, Checkbox, A, DataList, Option, StyleRenderer } from 'alkali'
import TextInputControl from 'components/generic/TextInputControl'

import OutcomeRows from './OutcomeRows'
import ExtractAnnotationModel from './ExtractAnnotationModel'
import ExtractionModel from './model/ExtractionModel'
import { importedCollections, liveParsed } from '../../model/SearchQuery'
import Icon from 'components/Icon'
import TextAreaControl from 'components/generic/TextAreaControl'
import { CollapsibleSection } from 'components/generic/CollapsibleSection'
import { ExtractionHistory } from './ExtractionHistory'
import { Chat } from 'integrations/intercom'
import { FieldType, SUPPORTED_ROW_TYPES } from 'services/extract/types'
import * as loadingIcon from 'icons/loading-half.svg'
import * as info from 'icons/information-circle.svg'
import * as multipleUsers from 'icons/multiple-neutral-2.svg'
import SelectivityDropdown from 'components/generic/SelectivityDropdown'
import * as pluralize from 'pluralize'
import { groupToStr as egroupToStr } from 'services/extract/util'
import type { ParsedQuery } from 'util/terms'
import noty from 'components/generic/noty'
import { track } from 'components/tracking/track'

export interface ExtractionFormOpts extends ElementProperties<ExtractionForm> {
	extractionModel: ExtractionModel
	annotModel: ExtractAnnotationModel
	activeIndex: VNumber
	debug?: boolean
}

const FILTER_COLLECTIONS = ['all', 'manual entry', 'mine', 'uploaded', 'my-uploaded']

const PROHIBITED_COLLECTIONS = [
	'all collections', 'uploaded studies', 'my uploaded studies', 'rapid extractions', 'my rapid extractions'
]

const collectionValid = c => {
	c = (c || '').trim().toLowerCase()
	return !FILTER_COLLECTIONS.some(f => f == c) && !PROHIBITED_COLLECTIONS.some(p => p == c)
}

class ExtractionButton extends Button('.submit-extraction').with({ type: 'button' }) {
	created(props) {
		const busy = new Variable(false)
		const showPopup = new Variable(false)
		const { extractionModel, error, debug } = props
		const validCollection = props.collection.to(collectionValid)
		const valid = and(extractionModel.canSubmit, validCollection)

		const isEdit = extractionModel.editingExtraction
		const hasChanges = extractionModel.hasChanges

		Object.assign(props, {
			title: all([extractionModel.canSubmit, extractionModel.errors], (canSubmit, e) =>
				canSubmit ? 'Submit extraction' : e?.length ? e.join('\n') : 'Cannot submit extraction'
			),
			disabled:  or(or(not(valid), busy),and(isEdit, not(hasChanges))),
			classes: {
				disabled: or(or(not(valid), busy),and(isEdit, not(hasChanges))),
				clickable: and(and(valid, not(busy)), or(not(isEdit),and(isEdit, hasChanges))),
				error: not(valid)
			},
			onclick(e) {
				
				if(extractionModel.editingExtraction.valueOf() && extractionModel.fromHistory.valueOf() != true)
					showPopup.put(true)
				else
					showPopup.put(false)
				
				if (showPopup.valueOf() == true) {  //EDIT
					noty({
						text: `Data for the outcome in this query has already been extracted and will be included in the analysis if this study is selected. If you would like to add another timepoint or variation of this outcome, please "Add" an outcome. If the population or outcome values are incorrect, proceed with editing.`,
						type: 'info',
						layout: 'topCenter',
						//closeWith: ['click', 'button'],
						buttons: [
						{
							text: 'Cancel',
							onClick(self) {
								self.close()
							}
						},
						{
							text: 'Add an Outcome',
							onClick(self) {
								//turn off editing
								extractionModel.editToggle.put(false)

								self.close()
							}
						},
						{
							text: 'Edit Values',
							onClick(self) {
								e.stopPropagation()
								if (!extractionModel.canSubmit.valueOf() || busy.valueOf()) return;
								busy.put(true)
								const isPrivate = extractionModel.summary.abstractSource.Private.valueOf()
								extractionModel.submit(debug, isPrivate ? props.collection.valueOf() : undefined).then(() =>
									error.put(null)
								).catch(e => {
									error.put({
										text: e.toString(),
										error: e
									})
								}).finally(() => busy.put(false))

								self.close()
							}
						}],
						callbacks: {
							afterClose() {
								//onSubmit?.()
							}
						},
						modal: true,
						timeout: 0
					})
				}
				else {  //ADD
					e.stopPropagation()
					if (!extractionModel.canSubmit.valueOf() || busy.valueOf()) return;
					busy.put(true)
					const isPrivate = extractionModel.summary.abstractSource.Private.valueOf()
					extractionModel.submit(debug, isPrivate ? props.collection.valueOf() : undefined).then(() =>
						error.put(null)
					).catch(e => {
						error.put({
							text: e.toString(),
							error: e
						})
					}).finally(() => busy.put(false))
				}
			}
		})
		props.content = _if(busy, [[Span('Saving...'), Icon('.spin-icon', { title: 'Saving...', icon: loadingIcon })],  [_if(extractionModel.editingExtraction, ['Edit Values', 'Submit Extraction'])]])
	}
}

//_if(extractionModel.editingExtraction, ['Editing', 'Adding'])

class InvalidNewGroupError extends Div('.error-msg') {
	created(props) {
		const { invalidNewGroups, studyCtx } = props
		props.classes = { hidden: not(invalidNewGroups.property('length')) }
		props.content = invalidNewGroups.to(invalidNewGroups => {
			const parsedQuery: ParsedQuery = liveParsed.valueOf()
			const question = parsedQuery?.annotations?.map(a => a.label || a).join('').trim()
			return invalidNewGroups?.length ? [
				'Add pre-extracted groups from the Groups icon ',
				Icon({ icon: multipleUsers }),
				'. Click ',
				A('.op-link', {
					onclick: () => {
						Chat.toggle('Please extract new ' + pluralize('group', invalidNewGroups.length) + ':\n« ' + invalidNewGroups.map(g => egroupToStr(g)).map(s => `'${s}'`).join(', ') + ' »\n\n' +
							'for study ' + studyCtx.study.id + ' ' + studyCtx.study.name + ` "${studyCtx.study.title}"` +
							(question ? `\n\n(query: ${question})` : ''))
						return false
					}
				}, 'here'),
				' to contact our team if you need additional groups.'
			] : []
		})
	}
}

export default class ExtractionTab extends Div('.extraction-tab') {
	created(props: ExtractionFormOpts) {
		const { extractionModel, annotModel, activeIndex, debug } = props
		const { extraction, invalidNewGroups, study: studyCtx } = extractionModel
		const invalidFieldTypes = extraction.outcomes.to(outcomes =>
			outcomes.some(o =>
				!SUPPORTED_ROW_TYPES.find(({ fieldType, rowType }) => o.type == fieldType && o.rowType == rowType)
			)
		).as(VArray)
		props.content = [
			Div({
				classes: {
					hidden: not(invalidFieldTypes.length)
				}
			}, invalidFieldTypes.map(t =>
				// if *any* of the outcome field types are unsupported, display message instead
				H2('.unsupported-type', [
					Icon({ icon: info }), /incidence/i.test(t) ? 'Incidence extraction: Coming soon.' : ('Unsupported Type: ' + t)
				])
			)),
			new ExtractionForm({
				classes: {
					hidden: invalidFieldTypes.length
				},
				extractionModel,
				annotModel,
				activeIndex,
				debug
			}),
			new InvalidNewGroupError({ invalidNewGroups, studyCtx }),
			CollapsibleSection('.extraction-history', {
				classes: { hidden: not(extractionModel.extractions.length) },
				open: false,
				header: 'Extraction History',
				body: new ExtractionHistory({ extractionModel, debug })
			}),
			//study ? new Flagging({ articleId: study.id }) : undefined
		]
	}
}

const userTeams = user =>
	_.sortBy(user.AllTeams || [], t => t.Name)

function permissionOptions(user) {
	return [
		{ text: 'Organization: ' + user.Client.Name, id: 'client:' + user.Client.Id },
		...userTeams(user).map(team => ({ text: 'Team: ' + team.Name, id: 'team:' + team.Id })),
		{ text: 'Myself', id: 'user:' + user.Id }
	]
}

class ExtractionForm extends Div('.extraction', {
	// must be declared in constructor, will fail in native classes in IE
	onfocusin(e) {
		this.annotModel.focusedState.updated()
	},
	onchange(e) {
		let groupRow = e.target.closest('.group-table-row')
		track({
			event: 'extract-edit-field',
			field: e.target.closest('.extract-field')?.className.replace(/\s*extract-field\s*/, ''),
			value: e.target.value,
			group: groupRow && groupRow.querySelector('.intervention-field.tt-input')?.value
		})
	}
}) {
	created(props: ExtractionFormOpts) {
		const { extractionModel, annotModel, activeIndex, debug, error = new Variable() } = props
		const { extraction, summary, status } = extractionModel
		const { study, studyCtx } = summary
		const focusedState = annotModel.focusedState.valueOf()
		const permissionOpts = permissionOptions(studyCtx.user)
		const collectionOpts = importedCollections.to(entries =>
			_.sortBy(_.uniq(entries.map(e => e.key)).filter(c => !FILTER_COLLECTIONS.some(e => e == c.toLowerCase())))
		)
		const collection = new Variable()
		const validCollection = collection.to(collectionValid)
		props.content = [
			Div('.status', {
				classes: {
					hidden: not(status.property('errors').property('length')),
					invalid: true
				}
			}, status.property('errors').to((errors = []) =>
				errors.filter(e => e != ExtractionModel.GROUP_ADD_DISALLOWED).map(e => Div(e))
			)),
			new OutcomeRows({ extractionModel, focusedState, activeIndex }),
			Div('.message-bar', [
				Div('.selecting', {
					hidden: error
				}, annotModel.focusedState.property('focused').to(focused => focused ? [
					Span('Selecting: '),
					// TODO: capture full path: "Selecting: Group 1 Intervention 1 Treatment 2", "Selecting: Outcome 1 Binary Value 2"
					Span('.dre-ds-ann.active.' + focused, _.startCase(focused))
				] : [
					Span('Choose field to extract value')
				])),
				Div('.error-msg', { classes: { hidden: not(error) } }, error.to(e => e ? e.content || Span({ title: e.error || e.text }, e.text) : ''))
			]),
			Div('.submission-bar', [
				_if(extractionModel.editingExtraction,
				[	null,
					[Label('Study note:'), new AutoHeighTextAreaControl('.note-input', { rows: 1 }, extraction.note)]
				]),
				new ExtractionButton({ extractionModel, error, debug, collection })
			]),
			!study ? Div('.private-toggle', [
				Div('.toggle-row', [
					Checkbox(summary.abstractSource.Private),
					Label({ title: 'Limit study visibility to' }, 'Limit study visibility to'),
					SelectivityDropdown({
						items: permissionOpts,
						value: summary.abstractSource.property('Permit'),
						showSearchInputInDropdown: false,
						readOnly: not(summary.abstractSource.Private),
						disabled: not(summary.abstractSource.Private),
						attributes: {
							disabled: not(summary.abstractSource.Private)
						}
					})
				]),
				Div('.collection-row', [
					Label('.collection-label', { for: '#collection', classes: { disabled: not(summary.abstractSource.Private) } }, 'Collection:'),
					TextInputControl({
						placeholder: 'optional collection',
						attributes: { list: 'study-collections' },
						classes: {
							'invalid-field': not(validCollection)
						},
						disabled: not(summary.abstractSource.Private)
					}, collection),
					DataList('#study-collections', collectionOpts.to(collections => collections.map(collection => Option([collection])))),
				])
			]) : undefined
		]
	}
}

class AutoHeightRenderer extends StyleRenderer {
	constructor(options) {
		super({
			...options,
			name: 'height',
			variable: options.variable.to(txt => {
				options.element.style.height = 'auto'
				return txt ? (options.element.scrollHeight) + 'px' : 'auto'
			})
		})
	}
}

class AutoHeighTextAreaControl extends TextAreaControl {
	created(properties) {
		new AutoHeightRenderer({
			element: this,
			variable: properties.content,
			updateOnStart: false
		})
	}
}